import { useCallback, useRef, useState } from 'react';
import { useDocumentRewrite } from '../../../hooks/index.js';

/**
 * @param {object} p
 * @param {import('axios').AxiosInstance|null|undefined} p.apiClient
 * @param {{ warning: Function, error: Function, success: Function }} p.toast
 * @param {string|null|undefined} p.currentDocumentId
 * @param {object|null|undefined} p.currentDocument
 * @param {string} p.documentPanelContent
 * @param {(id: string, patch: object) => void} p.updateDocument
 */
export function useAssistantDocumentEditor({
  apiClient,
  toast,
  currentDocumentId,
  currentDocument,
  documentPanelContent,
  updateDocument,
}) {
  const [documentEditorResetKey, setDocumentEditorResetKey] = useState(0);
  const [saveToDriveModalOpen, setSaveToDriveModalOpen] = useState(false);
  const saveToDriveContentRef = useRef({ html: '', plainText: '' });

  const documentRewriteAgentUuid = (import.meta.env.VITE_DOCUMENT_REWRITE_AGENT_UUID ?? '').trim();
  const documentAiRewriteEnabled = Boolean(documentRewriteAgentUuid);

  const handleDocumentRewriteReplace = useCallback(
    (newText) => {
      if (!currentDocumentId) return;
      updateDocument(currentDocumentId, { content: newText, contentFormat: 'text' });
      setDocumentEditorResetKey((k) => k + 1);
    },
    [currentDocumentId, updateDocument],
  );

  const getDocumentBodyForRewrite = useCallback(() => {
    return String(currentDocument?.content ?? documentPanelContent ?? '');
  }, [currentDocument?.content, documentPanelContent]);

  const { rewrite: rewriteDocumentFromAi, isRewriting: isDocumentAiRewriting } = useDocumentRewrite({
    apiClient,
    rewriteAgentUuid: documentRewriteAgentUuid,
    getContent: getDocumentBodyForRewrite,
    onReplaceContent: handleDocumentRewriteReplace,
  });

  const handleDocumentAiInstruction = useCallback(
    async (instruction) => {
      if (!currentDocumentId) {
        toast.warning({
          message: 'No hay un documento identificado para guardar el resultado.',
        });
        return;
      }
      try {
        await rewriteDocumentFromAi(instruction);
      } catch (err) {
        console.error(err);
        toast.error({
          message: err?.message ?? 'No se pudo aplicar la mejora con IA al documento.',
        });
      }
    },
    [currentDocumentId, rewriteDocumentFromAi, toast],
  );

  const handleDocumentContentPayloadChange = useCallback(
    (payload) => {
      if (!currentDocumentId) return;
      const nextContent = payload?.html ?? payload?.text ?? '';
      const nextFormat = payload?.html ? 'html' : 'text';
      updateDocument(currentDocumentId, {
        content: nextContent,
        contentFormat: nextFormat,
      });
    },
    [currentDocumentId, updateDocument],
  );

  const handleOpenSaveToDriveFromEditor = useCallback(({ html, plainText }) => {
    saveToDriveContentRef.current = {
      html: typeof html === 'string' ? html : '',
      plainText: typeof plainText === 'string' ? plainText : '',
    };
    setSaveToDriveModalOpen(true);
  }, []);

  const handleSaveToDriveModalClose = useCallback(() => setSaveToDriveModalOpen(false), []);

  const getSaveToDriveDocumentContent = useCallback(() => {
    const { html } = saveToDriveContentRef.current;
    return { content: html || '', contentFormat: 'html' };
  }, []);

  const handleSaveToDriveDone = useCallback(
    (result) => {
      if (result?.successCount > 0) {
        const n = result.successCount;
        const base =
          n === 1
            ? 'Document saved to your workspace.'
            : `Document saved to ${n} locations.`;
        if (result.errors?.length) {
          toast.warning({
            message: `${base} Some locations failed: ${result.errors.slice(0, 2).join('; ')}`,
          });
        } else {
          toast.success({ message: base });
        }
        return;
      }
      const err =
        result?.errors?.join('\n') || result?.error?.message || 'Could not save to drive.';
      toast.error({ message: err });
    },
    [toast],
  );

  return {
    documentEditorResetKey,
    documentAiRewriteEnabled,
    isDocumentAiRewriting,
    handleDocumentAiInstruction,
    handleDocumentContentPayloadChange,
    saveToDriveModalOpen,
    handleOpenSaveToDriveFromEditor,
    handleSaveToDriveModalClose,
    getSaveToDriveDocumentContent,
    handleSaveToDriveDone,
  };
}
